/**
 * WishlistLegend — Floating banner showing active wishlists in the viewer.
 *
 * Renders colored chips per person (e.g., 🩷 Sarah (3) 💜 Lina (2)).
 * Tapping a chip toggles that person's markers. The ✕ dismisses the banner.
 *
 * Also handles loading wishlists on mount via wishlistStore.
 */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useWishlistStore } from '@/stores/wishlistStore'
import './WishlistLegend.css'

interface WishlistLegendProps {
  shopId: string
}

export function WishlistLegend({ shopId }: WishlistLegendProps) {
  const { t } = useTranslation('delivery')
  const activeWishlists = useWishlistStore((s) => s.activeWishlists)
  const loading = useWishlistStore((s) => s.loading)
  const bannerDismissed = useWishlistStore((s) => s.bannerDismissed)
  const loadWishlistsForShop = useWishlistStore((s) => s.loadWishlistsForShop)
  const toggleWishlistVisibility = useWishlistStore((s) => s.toggleWishlistVisibility)
  const removeActiveWishlist = useWishlistStore((s) => s.removeActiveWishlist)
  const dismissBanner = useWishlistStore((s) => s.dismissBanner)

  // Load wishlists for this shop on mount
  useEffect(() => {
    loadWishlistsForShop(shopId)
  }, [shopId, loadWishlistsForShop])

  // Don't render if dismissed, loading, or no wishlists
  if (bannerDismissed || loading || activeWishlists.length === 0) return null

  return (
    <div className="wishlist-legend">
      <span className="wishlist-legend-icon">🎁</span>
      <div className="wishlist-legend-chips">
        {activeWishlists.map((wl) => (
          <button
            key={wl.token}
            className={`wishlist-legend-chip ${!wl.visible ? 'wishlist-legend-chip-off' : ''}`}
            style={{
              '--chip-color': wl.color,
              borderColor: wl.visible ? wl.color : '#475569',
              background: wl.visible ? `${wl.color}20` : 'transparent',
            } as React.CSSProperties}
            onClick={() => toggleWishlistVisibility(wl.token)}
            title={wl.visible ? t('wishBanner.tapToHide') : t('wishBanner.tapToShow')}
          >
            <span className="wishlist-legend-chip-dot" style={{ background: wl.visible ? wl.color : '#475569' }} />
            <span className="wishlist-legend-chip-name">
              {wl.senderName || t('wishBanner.unknown')}
            </span>
            <span className="wishlist-legend-chip-count">({wl.items.length})</span>
            <button
              className="wishlist-legend-chip-remove"
              onClick={(e) => { e.stopPropagation(); removeActiveWishlist(wl.token) }}
              title={t('wishBanner.remove')}
            >
              ×
            </button>
          </button>
        ))}
      </div>
      <button className="wishlist-legend-dismiss" onClick={dismissBanner} title={t('wishBanner.dismiss')}>
        ✕
      </button>
    </div>
  )
}
